/**
 * Types mode — library browser for declared kinds.
 *
 * Step 2: tree on the left, detail-with-preview-row on the right.
 *
 * Tree groups types by their base kind (module / surface / interface /
 * process / view), resolved by walking each TypeDef's parentType chain
 * back to a built-in root.
 *
 * Detail shows a header with the kind chain breadcrumb and a row of
 * three preview cells (DOT / COMPACT / FULL). Each cell renders the
 * type's widget at fixed size, with `--arch-band-*` CSS variables set
 * to the values they would have at that conceptual zoom band on the
 * real board. Architects see exactly how the widget unfolds across
 * LOD without having to scroll the board.
 *
 * Sample data: prefer the first real instance of the kind found in the
 * loaded model. Fall back to a synthesized placeholder when none
 * exists. (Sample-data picker — choosing among multiple instances —
 * lands in a later step.)
 */
import { useMemo, useState } from "react";
import type {
  ArchitectureModel,
  FieldEntry,
  Interface,
  Module,
  StableId,
  TypeDef,
  TypeFieldEntry,
  TypeAspectEntry,
} from "@archlang/scene/arch-model";
import {
  BASE_TYPE_COLOR,
  type BaseType,
  classifyType,
  filterPreviewableTypes,
  flattenInterfaces,
  flattenModulesWithParent,
  moduleKey,
  valueToString,
  withBaseTypeStubs,
} from "@archlang/scene/arch-model";
import { TemplateNode, WidgetNode } from "../board/WidgetNode";
import {
  widgetForInterface,
  widgetForModule,
  type WidgetRender,
} from "../board/widgetResolve.js";
import { aggregateInterfaceWidget } from "@archlang/scene/arch-model";
import { interfaceDisplayName } from "../displayNames";
import { EdgesLayer, type ItemRenderContext } from "../board/SpaceRenderer";
import type { BoardEdge, BoardItem } from "@archlang/scene/board";
import { resolveNodeSize } from "@archlang/scene/board";
import { NODE_W as DEFAULT_NODE_W, NODE_H as DEFAULT_NODE_H } from "@archlang/scene/board";
import {
  descriptionToPlainText,
  handleDescriptionClick,
  renderDescriptionHtml,
} from "../description/render.js";
import { bareTypeName, typeSelectionKey } from "../typeKey.js";

interface TypeRow {
  readonly type: TypeDef;
  readonly baseType: BaseType;
  readonly chain: readonly string[];
}

function rowFor(type: TypeDef, byName: ReadonlyMap<string, TypeDef>): TypeRow {
  const c = classifyType(type, byName);
  return { type, baseType: c.baseType, chain: c.chain };
}

interface TypesModeProps {
  readonly model: ArchitectureModel | null;
  /** Switch to Board mode and fly the board to a given module instance. */
  readonly onJumpToInstance?: (moduleId: StableId) => void;
  /** Currently selected type — a selection key (`Space::name`) or a bare
   *  name — supplied by the workspace panel. */
  readonly selectedTypeName?: string | null;
  /** Navigate to an `archlang:` URI from a rendered description link. */
  readonly onArchlangNav?: (href: string) => void;
}

export function TypesMode({
  model,
  onJumpToInstance,
  selectedTypeName,
  onArchlangNav,
}: TypesModeProps) {
  // Augment the declared/imported types with synthetic stubs for the
  // five built-in base kinds, so the type browser surfaces bare
  // `module` / `interface` / `surface` / `process` / `view` alongside
  // user-declared and library-imported types.
  const allTypes = useMemo(
    () => (model ? filterPreviewableTypes(withBaseTypeStubs(model.types)) : []),
    [model],
  );

  const byName = useMemo(() => {
    const m = new Map<string, TypeDef>();
    for (const t of allTypes) m.set(t.name, t);
    return m;
  }, [allTypes]);

  const rows: readonly TypeRow[] = useMemo(() => {
    return allTypes
      .map((t) => rowFor(t, byName))
      .sort((a, b) => a.type.name.localeCompare(b.type.name));
  }, [allTypes, byName]);

  // Type selection is controlled by the workspace panel. The key is
  // space-qualified (`Space::name`) so two spaces' same-named types stay
  // distinct entities; bare names still land on their first match (r56).
  // Fall back to the first row so the main pane always shows something.
  const effectiveSelected =
    selectedTypeName ?? (rows[0] ? typeSelectionKey(rows[0].type) : null);
  const selected: TypeRow | null = effectiveSelected
    ? rows.find((r) => typeSelectionKey(r.type) === effectiveSelected) ??
      rows.find((r) => r.type.name === bareTypeName(effectiveSelected)) ??
      null
    : null;

  // Flat list of every module instance (children flattened) so we can
  // pick the first matching instance per kind for the preview.
  const flatModules: readonly Module[] = useMemo(() => {
    if (!model) return [];
    return flattenModulesWithParent(model.modules);
  }, [model]);

  // Flat list of every interface instance across modules + surfaces.
  // Interfaces aren't modules — they live as members of modules — so a
  // separate index is needed for instance counts and preview samples.
  const flatInterfaces: readonly { iface: Interface; parent: Module }[] = useMemo(() => {
    const out: { iface: Interface; parent: Module }[] = [];
    for (const m of flatModules) {
      for (const i of flattenInterfaces(m)) out.push({ iface: i, parent: m });
    }
    return out;
  }, [flatModules]);

  return (
    <main className="min-h-0 flex-1 overflow-y-auto bg-slate-950 text-slate-100">
      <div className="mx-auto w-full max-w-6xl px-6 py-8 sm:px-8">
        {!selected && (
          <div className="text-slate-400">
            <h1 className="mb-2 text-2xl font-semibold text-slate-100">
              Type library
            </h1>
            <p className="text-sm leading-relaxed">
              Select a type from the workspace panel to see its widget
              rendered at every zoom level, alongside its kind chain.
            </p>
          </div>
        )}
        {selected && (
          <TypeDetail
            row={selected}
            flatModules={flatModules}
            flatInterfaces={flatInterfaces}
            onJumpToInstance={onJumpToInstance}
            onArchlangNav={onArchlangNav}
          />
        )}
      </div>
    </main>
  );
}

interface TypeDetailProps {
  readonly row: TypeRow;
  readonly flatModules: readonly Module[];
  readonly flatInterfaces: readonly { iface: Interface; parent: Module }[];
  readonly onJumpToInstance?: (moduleId: StableId) => void;
  readonly onArchlangNav?: (href: string) => void;
}

function TypeDetail({
  row,
  flatModules,
  flatInterfaces,
  onJumpToInstance,
  onArchlangNav,
}: TypeDetailProps) {
  const isInterface = row.baseType === "interface";

  // Sample picking: modules look at flatModules; interfaces look at
  // flatInterfaces (they live inside modules/surfaces, not at module level).
  const sampleInstance = useMemo<Module | null>(() => {
    if (isInterface) {
      const hit = flatInterfaces.find((e) => e.iface.typeName === row.type.name);
      return hit ? interfaceAsModule(hit.iface) : null;
    }
    return flatModules.find((m) => m.typeName === row.type.name) ?? null;
  }, [isInterface, flatInterfaces, flatModules, row.type.name]);

  const sample: Module = useMemo(
    () => sampleInstance ?? synthesizeSample(row),
    [sampleInstance, row],
  );

  const instanceCount = useMemo(() => {
    if (isInterface) {
      return flatInterfaces.filter((e) => e.iface.typeName === row.type.name).length;
    }
    return flatModules.filter((m) => m.typeName === row.type.name).length;
  }, [isInterface, flatInterfaces, flatModules, row.type.name]);

  return (
    <div className="w-full min-w-0">
      <header className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">
          type
        </div>
        <h1 className="text-3xl font-semibold text-slate-100">
          {row.type.name}
        </h1>
        <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
          <span>kind chain:</span>
          {row.chain.map((seg, i) => {
            const isBase = i === 0;
            const isSelf = i === row.chain.length - 1;
            return (
              <span key={seg + i} className="flex items-center gap-2">
                {i > 0 && <span className="text-slate-600">→</span>}
                <span
                  className={
                    isSelf
                      ? "text-sky-300 font-medium"
                      : isBase
                        ? BASE_TYPE_COLOR[row.baseType]
                        : "text-slate-300"
                  }
                >
                  {seg}
                </span>
              </span>
            );
          })}
        </div>
        {(row.type.space || row.type.exported) && (
          <div className="mt-2 flex items-center gap-2 text-xs">
            {row.type.space && (
              <span className="rounded-sm bg-slate-800 px-1.5 py-0.5 text-slate-300">
                space: {row.type.space}
              </span>
            )}
            {row.type.exported && (
              <span className="rounded-sm bg-emerald-500/15 px-1.5 py-0.5 text-emerald-300">
                exported
              </span>
            )}
          </div>
        )}
        <div className="mt-2 text-xs text-slate-500">
          {instanceCount === 0
            ? "No instances declared yet — preview uses a synthesized example."
            : `${instanceCount} instance${instanceCount === 1 ? "" : "s"} in this workspace.`}
        </div>
        {row.type.description && (
          <div
            className="arch-desc mt-3 text-sm text-slate-300 leading-snug"
            onClick={
              onArchlangNav
                ? (e) => handleDescriptionClick(e, onArchlangNav)
                : undefined
            }
            dangerouslySetInnerHTML={{
              __html: renderDescriptionHtml(row.type.description),
            }}
          />
        )}
      </header>

      {row.baseType === "module" && (
        <section className="mb-8">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">
            Widget preview
          </div>
          <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))]">
            <PreviewCell band="dot" sample={sample} widget={widgetForModule(sample)} />
            <PreviewCell band="compact" sample={sample} widget={widgetForModule(sample)} />
            <PreviewCell band="full" sample={sample} widget={widgetForModule(sample)} />
          </div>
        </section>
      )}

      {isInterface && (
        <InterfacePreviewSection type={row.type} sample={sample} />
      )}

      <SchemaSection type={row.type} />

      <UsedBySection
        type={row.type}
        flatModules={flatModules}
        onJumpToInstance={onJumpToInstance}
      />
    </div>
  );
}

// =============================================================================
// Used-by section
// =============================================================================

function UsedBySection({
  type,
  flatModules,
  onJumpToInstance,
}: {
  type: TypeDef;
  flatModules: readonly Module[];
  onJumpToInstance?: (moduleId: StableId) => void;
}) {
  const instances = useMemo(
    () => flatModules.filter((m) => m.typeName === type.name),
    [flatModules, type.name],
  );
  if (instances.length === 0) return null;
  return (
    <section className="mt-8">
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
        Used by · {instances.length}
      </div>
      <ul className="rounded-md border border-slate-800 bg-slate-900/(--tint-muted) divide-y divide-slate-800">
        {instances.map((m) => {
          const id = moduleKey(m) as StableId;
          return (
            <li key={id}>
              <button
                onClick={() => onJumpToInstance?.(id)}
                disabled={!onJumpToInstance}
                className={
                  "w-full text-left px-3 py-2 text-sm flex items-center gap-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 " +
                  (onJumpToInstance
                    ? "hover:bg-slate-800/(--tint-dim) text-slate-200 cursor-pointer"
                    : "text-slate-400 cursor-default")
                }
                title={onJumpToInstance ? "Show on board" : undefined}
              >
                <span className="font-medium">{m.name}</span>
                {m.description && (
                  <span className="text-xs text-slate-500 truncate">
                    {descriptionToPlainText(m.description)}
                  </span>
                )}
                <span className="flex-1" />
                {onJumpToInstance && (
                  <span className="text-xs text-sky-400">→ board</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

type Band = "dot" | "compact" | "full";

const BAND_LABEL: Record<Band, string> = {
  dot: "Dot",
  compact: "Compact",
  full: "Full",
};

const BAND_DESC: Record<Band, string> = {
  dot: "Far-zoomed (e.g. seeing the whole system)",
  compact: "Mid-zoomed (a section in view)",
  full: "Up close (focal item)",
};

// Locked scale per band — picked so the widget's CSS bands (smoothstep
// over BAND_COMPACT_START..BAND_COMPACT_REVEAL_END and
// BAND_FULL_START..BAND_FULL_REVEAL_END against `--arch-scale`; values
// in board/lodBands.ts) settle at the correct visual: dot collapses
// body & details, compact shows body, full reveals details.
const BAND_SCALE: Record<Band, number> = {
  dot:     0.30,
  compact: 0.80,
  full:    1.20,
};

// Cell box height — wide enough (via fluid width) to hold the widget at
// full band with breathing room. Width is fluid: the grid auto-fits cells
// at a min that exceeds the largest projected widget, so nothing clips.
const PREVIEW_H = 220;

function PreviewCell({
  band,
  sample,
  widget,
}: {
  band: Band;
  sample: Module;
  widget: WidgetRender;
}) {
  const scale = BAND_SCALE[band];
  // Widgets without an explicit size fall back to the layout solver's
  // NODE_W/NODE_H, matching what the user actually sees on the board.
  const modelW = resolveNodeSize(widget.spec, sample).w || DEFAULT_NODE_W;
  const modelH = resolveNodeSize(widget.spec, sample).h || DEFAULT_NODE_H;
  const projW = modelW * scale;
  const projH = modelH * scale;

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div
        className="relative flex w-full items-center justify-center overflow-hidden rounded-lg border border-slate-800 bg-slate-900/(--tint-muted)"
        style={{ height: PREVIEW_H }}
      >
        {/* Mirror the board: render the widget at its MODEL size and
         * apply a CSS scale transform on the wrapper. This keeps every
         * relative unit (cqh / cqw / em) and any rounding behaviour
         * identical to what the board produces — forcing the host to
         * `projW × projH` instead would give the widget a different
         * layout box and the preview wouldn't match the live render. */}
        <div
          style={{
            width: modelW,
            height: modelH,
            transform: `scale(${scale})`,
            transformOrigin: "center center",
          }}
        >
          {widget.kind === "tag" ? (
            <WidgetNode
              tag={widget.tag}
              spec={widget.spec}
              module={sample}
              scale={scale}
            />
          ) : (
            <TemplateNode
              template={widget.template}
              module={sample}
              scale={scale}
            />
          )}
        </div>
      </div>
      <div className="text-xs">
        <div className="font-semibold text-slate-200">
          {BAND_LABEL[band]}{" "}
          <span className="text-slate-500 font-normal">
            · {Math.round(projW)}×{Math.round(projH)}px
          </span>
        </div>
        <div className="text-slate-500">{BAND_DESC[band]}</div>
      </div>
    </div>
  );
}

/**
 * Adapt an Interface instance to the Module shape the preview pipeline
 * expects. Interfaces and modules share the field/label/name shape that
 * widgets actually read off `el.arch`; the extra Module-only fields
 * (surfaces, interfaces, children) are filled with empty arrays so the
 * type compiles and any defensive widget code that reads them doesn't
 * crash.
 */
function interfaceAsModule(iface: Interface): Module {
  return {
    // Friendly `(unspecified)` placeholder for an anonymous handler so the
    // preview card doesn't surface the raw `(anonymous)` sentinel.
    name: interfaceDisplayName(iface),
    typeName: iface.typeName,
    baseType: "interface",
    description: iface.description,
    fields: iface.fields,
    aspects: iface.aspects,
    surfaces: [],
    interfaces: [],
    children: [],
  };
}

/**
 * Build a synthetic Module instance for a TypeDef when no real
 * instance exists. The result is shape-compatible with the preview
 * pipeline — widgets read it via `el.arch` exactly like a real
 * instance.
 *
 * For required cascade fields the synthesizer fills a placeholder
 * value (`"example"`), so widgets that read e.g. `team` get something
 * to render in their preview rather than blank slots.
 */
function synthesizeSample(row: TypeRow): Module {
  const fields: FieldEntry[] = [];
  for (const f of row.type.fields ?? []) {
    if (f.value !== undefined) {
      fields.push({ path: f.path, value: f.value });
    } else if (f.required) {
      fields.push({ path: f.path, value: placeholderForField(f.path) });
    }
  }
  const aspects: { path: readonly string[]; value: unknown }[] = [];
  for (const l of row.type.aspects ?? []) {
    if (l.value !== undefined) {
      aspects.push({ path: l.path, value: l.value });
    } else if (l.required) {
      aspects.push({ path: l.path, value: placeholderForField(l.path) });
    }
  }
  return {
    name: `Example${capitalize(row.type.name)}`,
    typeName: row.type.name,
    baseType: row.baseType,
    description: `Synthesized preview of a ${row.type.name}`,
    fields,
    aspects,
    surfaces: [],
    interfaces: [],
    children: [],
  };
}

function placeholderForField(path: readonly string[]): string {
  return path[path.length - 1] ?? "example";
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// =============================================================================
// Schema section
// =============================================================================

function SchemaSection({ type }: { type: TypeDef }) {
  const fields = type.fields ?? [];
  const labels = type.aspects ?? [];
  if (fields.length === 0 && labels.length === 0) {
    return (
      <section className="text-xs text-slate-600">
        No schema declarations on this type.
      </section>
    );
  }
  return (
    <section className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
          Fields
        </div>
        {fields.length === 0 ? (
          <div className="text-xs text-slate-600">None.</div>
        ) : (
          <ul className="rounded-md border border-slate-800 bg-slate-900/(--tint-muted) divide-y divide-slate-800">
            {fields.map((f, i) => (
              <li key={f.path.join(".") + i} className="px-3 py-2 text-sm">
                <FieldRow field={f} />
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
          Labels
        </div>
        {labels.length === 0 ? (
          <div className="text-xs text-slate-600">None.</div>
        ) : (
          <ul className="rounded-md border border-slate-800 bg-slate-900/(--tint-muted) divide-y divide-slate-800">
            {labels.map((l, i) => (
              <li key={l.path.join(".") + i} className="px-3 py-2 text-sm">
                <LabelRow label={l} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function FieldRow({ field }: { field: TypeFieldEntry }) {
  return (
    <div className="flex items-center gap-3">
      <code className="font-mono text-slate-200">{field.path.join(".")}</code>
      <FlagBadges field={field} />
      <span className="flex-1" />
      {field.value !== undefined && (
        <span className="text-xs text-slate-400 truncate max-w-[40%]">
          = <code className="text-slate-300">{valueToString(field.value)}</code>
        </span>
      )}
    </div>
  );
}

function LabelRow({ label }: { label: TypeAspectEntry }) {
  return (
    <div className="flex items-center gap-3">
      <code className="font-mono text-slate-200">labels.{label.path.join(".")}</code>
      <div className="flex gap-1">
        {label.required && <Badge color="amber">required</Badge>}
        {label.drop && <Badge color="rose">drop</Badge>}
      </div>
      <span className="flex-1" />
      {label.value !== undefined && (
        <span className="text-xs text-slate-400 truncate max-w-[40%]">
          = <code className="text-slate-300">{valueToString(label.value)}</code>
        </span>
      )}
    </div>
  );
}

function FlagBadges({ field }: { field: TypeFieldEntry }) {
  return (
    <div className="flex gap-1">
      {field.required && <Badge color="amber">required</Badge>}
      {field.cascade && <Badge color="sky">cascade</Badge>}
      {field.append && <Badge color="emerald">append</Badge>}
      {!field.required && !field.cascade && !field.append && (
        <Badge color="slate">default</Badge>
      )}
    </div>
  );
}

const BADGE_CLASSES: Record<string, string> = {
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  sky: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  rose: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  slate: "bg-slate-700/(--tint-muted) text-slate-400 border-slate-600/(--tint-muted)",
};

function Badge({ color, children }: { color: string; children: React.ReactNode }) {
  const cls = BADGE_CLASSES[color] ?? BADGE_CLASSES.slate;
  return (
    <span
      className={
        "text-[10px] uppercase tracking-wider font-semibold rounded-sm px-1.5 py-0.5 border " +
        cls
      }
    >
      {children}
    </span>
  );
}

// =============================================================================
// Interface preview (3 zoom bands when a widget is configured;
// otherwise a single styled-arrow fallback).
// =============================================================================

function InterfacePreviewSection({
  type,
  sample,
}: {
  type: TypeDef;
  sample: Module;
}) {
  // Resolve the widget from the sample's fields (real instance overrides
  // bubble up here) — falls through to type.fields when synthesized.
  const widget = widgetForInterface({ fields: sample.fields });
  return (
    <section className="mb-8">
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">
        {widget ? "Widget preview" : "Edge preview"}
      </div>
      {widget ? (
        <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))]">
          <PreviewCell band="dot" sample={sample} widget={widget} />
          <PreviewCell band="compact" sample={sample} widget={widget} />
          <PreviewCell band="full" sample={sample} widget={widget} />
        </div>
      ) : (
        <EdgePreview type={type} />
      )}
    </section>
  );
}

// =============================================================================
// Edge preview (interface base kind, no-widget fallback)
// =============================================================================

/**
 * Multi-case preview for interface types. Builds a tiny synthetic board
 * for each routing geometry the real diagram produces — short straight,
 * gentle curve, two-way (bidirectional), and back-edge — and renders
 * them through the *actual* board edge renderer (`EdgesLayer`). The
 * preview is therefore pixel-exact with the board: same router, same
 * style resolution, same arrowheads, same midpoint badge/overlay. No
 * geometry is reimplemented here; we only place two nodes per cell and
 * let `planRoutes` inside `EdgesLayer` do the rest.
 *
 * Edge widget config (style + tag/template + icon/text badge props)
 * comes from the type's `widget.{...}` cascade fields, aggregated the
 * same way the layout adapter aggregates a real edge's target
 * interfaces.
 */
const PREVIEW_CELL_W = 360;
const PREVIEW_CELL_H = 180;
const PREVIEW_NODE_W = 64;
const PREVIEW_NODE_H = 56;
const PREVIEW_SIDE_PAD = 24;

// EdgesLayer expects a full render context; the preview is read-only so
// every callback is a no-op and every interaction-state field is empty.
const NOOP_EDGE_CTX: ItemRenderContext = {
  selectedId: null,
  selectedEdgeId: null,
  activeProcessId: null,
  activeStepIndex: null,
  highlighted: new Set(),
  diagnosticsByItem: new Map(),
  onSelect: () => {},
  onSelectEdge: () => {},
  onDive: () => {},
  onPlace: () => {},
  onUnpin: () => {},
  pinnedIds: new Set(),
};

function previewNode(id: string, label: string, leftEdge: number, yCentre: number): BoardItem {
  return {
    id,
    label,
    x: leftEdge,
    y: yCentre - PREVIEW_NODE_H / 2,
    w: PREVIEW_NODE_W,
    h: PREVIEW_NODE_H,
  };
}

const PREVIEW_LEFT_X = PREVIEW_SIDE_PAD;
const PREVIEW_RIGHT_X = PREVIEW_CELL_W - PREVIEW_SIDE_PAD - PREVIEW_NODE_W;
const PREVIEW_MID_Y = PREVIEW_CELL_H / 2;

function EdgePreview({ type }: { type: TypeDef }) {
  const { makeEdge, style } = useMemo(() => {
    const fields: FieldEntry[] = (type.fields ?? [])
      .filter((f): f is TypeFieldEntry & { value: unknown } => f.value !== undefined)
      .map((f) => ({ path: f.path, value: f.value }));
    const widget = aggregateInterfaceWidget([{ fields }]);
    const make = (from: string, to: string, id: string): BoardEdge => ({
      id,
      from,
      to,
      details: [],
      ...(widget.style ? { widgetStyle: widget.style } : {}),
      ...(widget.tag ? { widgetTag: widget.tag } : {}),
      ...(widget.template ? { widgetTemplate: widget.template } : {}),
      ...(widget.props ? { widgetProps: widget.props } : {}),
    });
    const iconRaw = widget.props?.get("icon");
    const textRaw = widget.props?.get("text");
    const footerStyle: EdgeStyle = {
      color: typeof widget.style?.color === "string" ? widget.style.color : "#64748b",
      dashed: widget.style?.dashed === true,
      width: typeof widget.style?.width === "number" ? widget.style.width : 2.5,
      opacity: typeof widget.style?.opacity === "number" ? widget.style.opacity : 0.5,
      tag: widget.tag,
      icon: typeof iconRaw === "string" && iconRaw ? iconRaw : undefined,
      text:
        textRaw === undefined || textRaw === null || textRaw === ""
          ? undefined
          : String(textRaw),
    };
    return { makeEdge: make, style: footerStyle };
  }, [type.fields]);

  // Two-node layouts that coax the real router into each named geometry.
  const cells = [
    {
      label: "straight",
      items: [
        previewNode("a", "A", PREVIEW_LEFT_X, PREVIEW_MID_Y),
        previewNode("b", "B", PREVIEW_RIGHT_X, PREVIEW_MID_Y),
      ],
      edges: [makeEdge("a", "b", "a→b")],
    },
    {
      label: "curved",
      items: [
        previewNode("a", "A", PREVIEW_LEFT_X, PREVIEW_CELL_H * 0.3),
        previewNode("b", "B", PREVIEW_RIGHT_X, PREVIEW_CELL_H * 0.7),
      ],
      edges: [makeEdge("a", "b", "a→b")],
    },
    {
      label: "two-way",
      items: [
        previewNode("a", "A", PREVIEW_LEFT_X, PREVIEW_MID_Y),
        previewNode("b", "B", PREVIEW_RIGHT_X, PREVIEW_MID_Y),
      ],
      edges: [makeEdge("a", "b", "a→b"), makeEdge("b", "a", "b→a")],
    },
    {
      label: "back-edge",
      // Source on the right, target on the left, edge still a→b.
      items: [
        previewNode("a", "A", PREVIEW_RIGHT_X, PREVIEW_MID_Y),
        previewNode("b", "B", PREVIEW_LEFT_X, PREVIEW_MID_Y),
      ],
      edges: [makeEdge("a", "b", "a→b")],
    },
  ];

  return (
    <div className="space-y-3">
      <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(min(100%,320px),1fr))]">
        {cells.map((cell, i) => (
          <PreviewEdgeCell
            key={cell.label}
            label={cell.label}
            items={cell.items}
            edges={cell.edges}
            uid={`tp${i}`}
          />
        ))}
      </div>
      <EdgeStyleFooter style={style} typeName={type.name} />
    </div>
  );
}

/**
 * One preview cell: faint synthetic-node outlines plus the real
 * `EdgesLayer` rendering the edge(s) between them at 1:1 scale. This is
 * the same component the board uses, so the result is an exact preview.
 */
function PreviewEdgeCell({
  label,
  items,
  edges,
  uid,
}: {
  label: string;
  items: readonly BoardItem[];
  edges: readonly BoardEdge[];
  uid: string;
}) {
  const itemMap = useMemo(() => {
    const m = new Map<string, BoardItem>();
    for (const it of items) m.set(it.id, it);
    return m;
  }, [items]);
  return (
    <div className="min-w-0 overflow-hidden rounded-lg border border-slate-800 bg-slate-900/(--tint-muted)">
      <div className="flex justify-center overflow-hidden">
      <div className="relative shrink-0" style={{ width: PREVIEW_CELL_W, height: PREVIEW_CELL_H }}>
        {/* Faint node outlines so the cell reads as "edge between two
            boxes" rather than a floating arrow. Drawn behind EdgesLayer. */}
        <svg
          width={PREVIEW_CELL_W}
          height={PREVIEW_CELL_H}
          className="absolute inset-0"
          style={{ pointerEvents: "none" }}
        >
          {items.map((it) => (
            <rect
              key={it.id}
              x={it.x}
              y={it.y}
              width={it.w}
              height={it.h}
              rx={6}
              ry={6}
              fill="rgba(30, 41, 59, 0.6)"
              stroke="rgba(71, 85, 105, 0.6)"
              strokeWidth={1}
            />
          ))}
        </svg>
        <EdgesLayer
          edges={edges}
          items={itemMap}
          ctx={NOOP_EDGE_CTX}
          spaceW={PREVIEW_CELL_W}
          spaceH={PREVIEW_CELL_H}
          scale={1}
          idSuffix={uid}
        />
      </div>
      </div>
      <div className="px-3 py-1.5 text-[11px] text-slate-500 border-t border-slate-800">
        {label}
      </div>
    </div>
  );
}

interface EdgeStyle {
  readonly color: string;
  readonly dashed: boolean;
  readonly width: number;
  readonly opacity: number;
  readonly tag?: string;
  readonly icon?: string;
  readonly text?: string;
}

function EdgeStyleFooter({ style, typeName }: { style: EdgeStyle; typeName: string }) {
  return (
    <div className="rounded-md border border-slate-800 bg-slate-900/(--tint-muted) px-3 py-2 text-xs text-slate-500">
      <span className="text-slate-400">{typeName} ·</span>{" "}
      <span className="text-slate-400">stroke:</span>{" "}
      <code className="text-slate-300">{style.color}</code>
      {" · "}
      <span className="text-slate-400">width:</span>{" "}
      <code className="text-slate-300">{style.width}</code>
      {" · "}
      <span className="text-slate-400">dashed:</span>{" "}
      <code className="text-slate-300">{String(style.dashed)}</code>
      {" · "}
      <span className="text-slate-400">opacity:</span>{" "}
      <code className="text-slate-300">{style.opacity}</code>
      {style.icon && (
        <>
          {" · "}
          <span className="text-slate-400">icon:</span>{" "}
          <code className="text-slate-300">{style.icon}</code>
        </>
      )}
      {style.text && (
        <>
          {" · "}
          <span className="text-slate-400">text:</span>{" "}
          <code className="text-slate-300">{style.text}</code>
        </>
      )}
      {style.tag && (
        <>
          {" · "}
          <span className="text-slate-400">overlay:</span>{" "}
          <code className="text-slate-300">{style.tag}</code>
        </>
      )}
    </div>
  );
}
